import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './style.scss';

const moneyBRL = (centavos) => {
  const v = Number(centavos || 0) / 100;
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const onlyDigits = (s) => String(s || '').replace(/\D+/g, '');

export default function CadastroAluno() {
  const { search } = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => new URLSearchParams(search).get('token') || '', [search]);

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    nome: '',
    email: '',
    senha: '',
    cpf: '',
    telefone: '',
  });

  const [submitting, setSubmitting] = useState(false);

  const [pix, setPix] = useState(null); // { transactionId, pixCode, status, usuarioCriado, usuarioId }
  const [checking, setChecking] = useState(false);

  const pollRef = useRef(null);
  const lastStatusRef = useRef(null);
  const doneRef = useRef(false); // evita dupla navegação

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setChecking(false);
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);

    if (!token) {
      setError('Token ausente. Verifique o link enviado pela instituição.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/cadastro/preview', { params: { token } });
      setPreview(data);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.erro || 'Não foi possível validar o link de cadastro.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPreview();
    return () => clearPoll();
  }, [loadPreview, clearPoll]);

  const validarForm = () => {
    const nome = String(form.nome || '').trim();
    const email = String(form.email || '').trim();
    const senha = String(form.senha || '').trim();
    const cpf = onlyDigits(form.cpf);
    const telefone = onlyDigits(form.telefone);

    if (!nome || !email || !senha) {
      return 'Preencha nome, e-mail e senha.';
    }
    if (!email.includes('@')) return 'E-mail inválido.';
    if (temCobranca) {
      if (!cpf || !telefone) return 'Preencha CPF e telefone para gerar o PIX.';
      if (cpf.length !== 11) return 'CPF inválido (precisa ter 11 números).';
      if (telefone.length < 10) return 'Telefone inválido (com DDD).';
    }
    return '';
  };

  const finalizarSePagoECadastrado = useCallback(
    ({ status, usuario_criado, usuario_id }) => {
      // ✅ só finaliza quando estiver pago E usuário criado
      if (doneRef.current) return;

      if (status === 'COMPLETED' && usuario_criado) {
        doneRef.current = true;
        clearPoll();
        navigate('/cadastro/obrigado', { replace: true });
      }
    },
    [clearPoll, navigate]
  );

  const checkStatusOnce = useCallback(
    async (transactionId) => {
      if (!transactionId || doneRef.current) return;

      try {
        const { data } = await api.get('/public/cadastro-pago/status', {
          params: { transactionId },
        });

        const st = data?.status || null;
        const usuarioCriado = !!data?.usuario_criado;
        const usuarioId = data?.usuario_id ?? null;

        // atualiza UI se mudou
        const stamp = `${st}|${usuarioCriado ? '1' : '0'}|${usuarioId ?? ''}`;
        if (stamp !== lastStatusRef.current) {
          lastStatusRef.current = stamp;

          setPix((p) =>
            p
              ? { ...p, status: st || p.status, usuarioCriado, usuarioId }
              : { transactionId, pixCode: '', status: st || 'PENDING', usuarioCriado, usuarioId }
          );
        }

        // estados finais
        if (st === 'CANCELED' || st === 'EXPIRED') {
          clearPoll();
          setError('Pagamento cancelado/expirado. Gere um novo PIX para tentar novamente.');
          return;
        }

        finalizarSePagoECadastrado({
          status: st,
          usuario_criado: usuarioCriado,
          usuario_id: usuarioId,
        });
      } catch (e) {
        // polling não deve quebrar a tela
        console.warn('Falha ao consultar status:', e?.response?.data || e?.message);
      }
    },
    [clearPoll, finalizarSePagoECadastrado]
  );

  const startPolling = useCallback(
    (transactionId) => {
      clearPoll();
      setChecking(true);

      // consulta imediata
      checkStatusOnce(transactionId);

      // a cada 2s
      pollRef.current = setInterval(() => checkStatusOnce(transactionId), 2000);
    },
    [checkStatusOnce, clearPoll]
  );

  const iniciarPix = async () => {
    const msg = validarForm();
    if (msg) {
      alert(msg);
      return;
    }

    setSubmitting(true);
    setError('');
    doneRef.current = false;

    try {
      const payload = {
        token,
        nome: String(form.nome || '').trim(),
        email: String(form.email || '').trim().toLowerCase(),
        senha: String(form.senha || ''),
        cpf: onlyDigits(form.cpf),
        telefone: onlyDigits(form.telefone),
      };

      const { data } = await api.post('/public/cadastro-pago', payload);

      // Cadastro gratuito: usuário criado diretamente, sem PIX
      if (data?.completed) {
        doneRef.current = true;
        navigate('/cadastro/obrigado', { replace: true });
        return;
      }

      const transactionId = data?.transactionId;
      const pixCode = data?.pixCode;
      const status = data?.status || 'PENDING';

      if (!transactionId || !pixCode) {
        setError('Resposta inválida ao gerar PIX.');
        return;
      }

      lastStatusRef.current = null;

      setPix({
        transactionId,
        pixCode,
        status,
        usuarioCriado: false,
        usuarioId: null,
      });

      startPolling(transactionId);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.erro || 'Erro ao iniciar o pagamento via PIX.');
    } finally {
      setSubmitting(false);
    }
  };

  const copiarPix = async () => {
    if (!pix?.pixCode) return;
    try {
      await navigator.clipboard.writeText(pix.pixCode);
      alert('Copia e cola do PIX copiado!');
    } catch {
      alert('Não consegui copiar automaticamente. Selecione e copie manualmente.');
    }
  };

  const instituicaoNome = preview?.instituicao?.nome || 'sua instituição';
  const valorCentavos = preview?.valor_cadastro_aluno_centavos ?? 0;
  const temCobranca = preview?.tem_cobranca ?? false;

  const statusLabel = (st, usuarioCriado) => {
    if (!st) return '—';
    if (st === 'PENDING') return 'Aguardando pagamento';
    if (st === 'COMPLETED' && usuarioCriado) return 'Pagamento confirmado e cadastro concluído';
    if (st === 'COMPLETED') return 'Pagamento confirmado (finalizando cadastro...)';
    if (st === 'CANCELED') return 'Cancelado';
    if (st === 'EXPIRED') return 'Expirado';
    if (st === 'ERROR') return 'Erro no pagamento';
    return st;
  };

  if (loading) {
    return (
      <div className="cadastro-publico">
        <div className="cadastro-card">
          <div className="sk sk-title" />
          <div className="sk sk-line" />
          <div className="sk sk-line" />
          <div className="sk sk-box" />
        </div>
      </div>
    );
  }

  if (error && !preview) {
    return (
      <div className="cadastro-publico">
        <div className="cadastro-card">
          <h1>Cadastro Istudo</h1>
          <p className="muted">Não foi possível abrir este link de cadastro.</p>
          <div className="alert alert--danger">{error}</div>

          <div className="actions">
            <button type="button" className="btn" onClick={loadPreview}>
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  const pago = pix?.status === 'COMPLETED' && pix?.usuarioCriado;

  return (
    <div className="cadastro-publico">
      <div className="cadastro-card">
        <div className="top">
          <h1>Bem-vindo ao cadastro da plataforma Istudo</h1>
          <p className="muted">
            Você está se cadastrando pela instituição <b>{instituicaoNome}</b>.{' '}
            {temCobranca
              ? 'Para concluir o cadastro, é necessário realizar o pagamento do PIX.'
              : 'O cadastro é gratuito. Preencha seus dados para continuar.'}
          </p>

          <div className="pillRow">
            {temCobranca && (
              <span className="pill">
                Valor do cadastro: <b>{moneyBRL(valorCentavos)}</b>
              </span>
            )}
            {pix && (
              <span className={`pill ${pago ? 'pill--ok' : 'pill--warn'}`}>
                Status: <b>{statusLabel(pix?.status || 'PENDING', pix?.usuarioCriado)}</b>
              </span>
            )}
          </div>

          {error ? <div className="alert alert--danger">{error}</div> : null}
        </div>

        {/* PASSO 1 */}
        {!pix?.transactionId && (
          <>
            <h2>Seus dados</h2>
            <div className="grid">
              <div className="field">
                <label>Nome completo</label>
                <input
                  value={form.nome}
                  onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
                  placeholder="Digite seu nome"
                  autoComplete="name"
                />
              </div>

              <div className="field">
                <label>E-mail</label>
                <input
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="seuemail@exemplo.com"
                  autoComplete="email"
                />
              </div>

              <div className="field">
                <label>Senha</label>
                <input
                  type="password"
                  value={form.senha}
                  onChange={(e) => setForm((p) => ({ ...p, senha: e.target.value }))}
                  placeholder="Crie uma senha"
                  autoComplete="new-password"
                />
              </div>

              {temCobranca && (
                <>
                  <div className="field">
                    <label>CPF</label>
                    <input
                      value={form.cpf}
                      onChange={(e) => setForm((p) => ({ ...p, cpf: e.target.value }))}
                      placeholder="Somente números"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="field">
                    <label>Telefone (com DDD)</label>
                    <input
                      value={form.telefone}
                      onChange={(e) => setForm((p) => ({ ...p, telefone: e.target.value }))}
                      placeholder="(xx) xxxxx-xxxx"
                      inputMode="tel"
                      autoComplete="tel"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="actions">
              <button type="button" className="btn btn--primary" onClick={iniciarPix} disabled={submitting}>
                {submitting
                  ? temCobranca ? 'Gerando PIX...' : 'Cadastrando...'
                  : temCobranca ? 'Gerar PIX para concluir cadastro' : 'Concluir cadastro gratuito'}
              </button>

              <button type="button" className="btn" onClick={loadPreview} disabled={submitting}>
                Recarregar link
              </button>
            </div>
          </>
        )}

        {/* PASSO 2 */}
        {pix?.transactionId && (
          <>
            <h2>Pagamento via PIX</h2>
            <p className="muted">
              Copie o código abaixo e cole no app do seu banco. Assim que o pagamento for confirmado,
              seu cadastro será concluído automaticamente.
            </p>

            <div className="pixBox">
              <div className="pixHead">
                <div>
                  <div className="pixLabel">Copia e cola (PIX)</div>
                  <div className="pixMeta">
                    TransactionId: <b>{pix.transactionId}</b>
                  </div>
                </div>

                <button type="button" className="btn btn--sm" onClick={copiarPix}>
                  Copiar
                </button>
              </div>

              <textarea readOnly value={pix.pixCode || ''} />
            </div>

            <div className="actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => startPolling(pix.transactionId)}
                disabled={checking || doneRef.current}
              >
                {checking ? 'Verificando automaticamente...' : 'Verificar automaticamente'}
              </button>

              <button type="button" className="btn" onClick={() => checkStatusOnce(pix.transactionId)}>
                Atualizar status
              </button>
            </div>

            {!pago && checking && (
              <div className="alert alert--info">
                Estamos verificando seu pagamento automaticamente a cada 2 segundos…
              </div>
            )}
          </>
        )}

        <div className="footNote">
          <small>Se você recebeu este link por engano, apenas feche esta página.</small>
        </div>
      </div>
    </div>
  );
}
